import { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, X, Images, Video } from 'lucide-react';

// Photo gallery with a large main image, thumbnail grid, and a keyboard-navigable lightbox.
export const PhotoGallery = ({ photos = [], alt = 'Listing photo', virtualTourUrl }) => {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const prev = useCallback(() => setIndex((i) => (i - 1 + photos.length) % photos.length), [photos.length]);
  const next = useCallback(() => setIndex((i) => (i + 1) % photos.length), [photos.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, prev, next]);

  const openAt = (i) => { setIndex(i); setOpen(true); };
  if (!photos.length) return null;

  return (
    <>
      <div className="grid gap-2 overflow-hidden rounded-2xl sm:grid-cols-4 sm:grid-rows-2 sm:h-[480px]">
        <button onClick={() => openAt(0)} className="group relative sm:col-span-2 sm:row-span-2 overflow-hidden" data-testid="gallery-main">
          <img src={photos[0]} alt={alt} className="h-72 w-full object-cover transition-transform duration-700 group-hover:scale-110 sm:h-full" />
        </button>
        {photos.slice(1, 5).map((p, i) => (
          <button key={i} onClick={() => openAt(i + 1)} className="group relative hidden sm:block overflow-hidden">
            <img src={p} alt={`${alt} ${i + 2}`} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
            {i === 3 && photos.length > 5 && (
              <span className="absolute inset-0 grid place-items-center bg-slate-900/60 text-sm font-semibold text-white backdrop-blur-sm">+{photos.length - 5} more</span>
            )}
          </button>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button onClick={() => openAt(0)} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-testid="view-all-photos">
          <Images size={16} /> View all {photos.length} photos
        </button>
        {virtualTourUrl && (
          <a
            href={virtualTourUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-5 py-2.5 text-sm font-medium text-blue-700 hover:bg-blue-100 transition-colors"
          >
            <Video size={16} /> View 3D / Virtual Tour
          </a>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl border-0 bg-transparent p-0 shadow-none" data-testid="listing-photo-lightbox">
          <div className="relative">
            <img src={photos[index]} alt={`${alt} ${index + 1}`} className="max-h-[85vh] w-full rounded-2xl object-contain" />
            <button onClick={() => setOpen(false)} className="absolute -top-4 -right-4 grid h-10 w-10 place-items-center rounded-full bg-white text-slate-700 shadow-xl hover:bg-slate-50 transition-colors" aria-label="Close"><X size={20} /></button>
            <button onClick={prev} className="absolute left-4 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/95 text-slate-800 shadow-xl hover:bg-white transition-colors" aria-label="Previous" data-testid="lightbox-prev"><ChevronLeft size={24} /></button>
            <button onClick={next} className="absolute right-4 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/95 text-slate-800 shadow-xl hover:bg-white transition-colors" aria-label="Next" data-testid="lightbox-next"><ChevronRight size={24} /></button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-slate-900/80 px-4 py-1.5 text-sm font-medium text-white backdrop-blur-sm">{index + 1} / {photos.length}</div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
